import React, { ReactNode, useState, useEffect, useMemo } from "react";
import {
  AppShell,
  Group,
  Button,
  Text,
  Center,
  UnstyledButton,
  Drawer,
  Stack,
  Avatar,
  Divider,
  NavLink,
  Menu,
  Container,
} from "@mantine/core";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  IconMenu2,
  IconHome,
  IconUser,
  IconSettings,
  IconLogout,
  IconTruck,
  IconChevronDown,
  IconUsers,
  IconCar,
  IconBuildingWarehouse,
  IconCurrencyDollar,
  IconPlug,
  IconChartPie,
  IconPackage,
  IconCheck,
  IconChevronRight,
} from "@tabler/icons-react";
import { RootState, AppDispatch } from "../store";
import { logout, setUser } from "../store/slices/authSlice";
import { ShipperResponse } from "../types/responses.type";
import config from "../config";
import shipperService from "../services/shipper.service";
import textLogo from "../assets/logo/text-logo.svg";
import logo from "../assets/logo/logo.svg";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import UserPropertiesModal from "./UserPropertiesModal";
import NavigationMenu, { NavigationMenuStruct } from "./NavigationMenu";

interface LayoutProps {
  children: ReactNode;
}

const HeaderShipperSelector = () => {
  const navigate = useNavigate();
  const isMdScreen = useMediaQuery("(min-width: 768px)");
  const [selectedShipper, setSelectedShipper] =
    useState<ShipperResponse | null>(null);
  const [shippers, setShippers] = useState<ShipperResponse[]>([]);
  const { user } = useSelector((state: RootState) => state.auth);

  const loadSelectedShipper = async () => {
    const shipperId = localStorage.getItem("owlara_selected_shipper");
    if (shipperId) {
      const response = await shipperService.getShipper(parseInt(shipperId));
      if (response.ok && response.data) {
        setSelectedShipper(response.data);
        // navigate("/");
      }
    }
  };

  const loadShippers = async () => {
    const response = await shipperService.getShippers();
    if (response.ok && response.data) {
      setShippers(response.data);
    }
  };

  useEffect(() => {
    if (!user) return;

    if (user && ["ADMIN", "SUPPORT"].includes(user.type)) {
      loadSelectedShipper();
      loadShippers();
    }
  }, [user]);

  if (!user || !["ADMIN", "SUPPORT"].includes(user.type)) {
    return null;
  }

  const handleSelectShipper = (shipper: ShipperResponse) => {
    localStorage.setItem(
      "owlara_selected_shipper",
      shipper.shipperId.toString()
    );
    setSelectedShipper(shipper);
    navigate("/");
    window.location.reload();
  };

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Button
          variant="subtle"
          color="blue"
          leftSection={<IconTruck size={16} />}
          rightSection={<IconChevronDown size={16} />}
        >
          {isMdScreen && `${selectedShipper?.companyName ?? "Select Shipper"}`}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        {shippers.map((shipper) => (
          <Menu.Item
            key={shipper.shipperId}
            onClick={() => handleSelectShipper(shipper)}
            leftSection={
              shipper.shipperId === selectedShipper?.shipperId ? (
                <IconCheck size={16} color="green" />
              ) : (
                <IconTruck size={16} />
              )
            }
          >
            {shipper.companyName}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};

const Layout = ({ children }: LayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const [drawerOpened, setDrawerOpened] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const [userProfileOpen, setUserProfileOpen] = useState(false);
  const [selectedShipper, setSelectedShipper] =
    useState<ShipperResponse | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const timezone = selectedShipper
    ? selectedShipper?.timezone
    : user?.shipper?.timezone;
  dayjs.tz.setDefault(timezone);
  useEffect(() => {
    const loadShipperInfo = async () => {
      if (user) {
        const shipperId = +(
          user.shipperId ??
          localStorage.getItem("owlara_selected_shipper") ??
          0
        );
        const response = await shipperService.getShipper(shipperId);
        if (response.ok && response.data) {
          setSelectedShipper(response.data);
        }
      }
    };
    loadShipperInfo();
  }, [user]);

  useEffect(() => {
    if (selectedShipper?.timezone || user?.shipper?.timezone) {
      dayjs.tz.setDefault(selectedShipper?.timezone ?? user?.shipper?.timezone);
    }
  }, [selectedShipper, user]);

  const handleLogout = async () => {
    await dispatch(logout());
    closeDrawer();
    navigate("/login");
  };

  const closeDrawer = () => setDrawerOpened(false);

  const mainMenu: NavigationMenuStruct[] = [
    { label: "Home", icon: <IconHome size="1rem" />, link: "/" },
    {
      label: "Orders",
      icon: <IconPackage size="1rem" />,
      link: "/orders",
    },
    {
      label: "Analysis",
      icon: <IconChartPie size="1rem" />,
      link: "/analysis",
      privateAccess: ["analysis.view"],
    },
    {
      label: "Trips",
      icon: <IconTruck size="1rem" />,
      link: "/trips",
    },
    {
      label: "Settings",
      icon: <IconSettings size="1rem" />,
      subMenus: [
        {
          label: "User Management",
          icon: <IconUsers size="1rem" />,
          link: "/settings/user-management",
          privateAccess: ["users.manage"],
        },
        {
          label: "Couriers",
          icon: <IconTruck size="1rem" />,
          link: "/settings/couriers",
        },
        {
          label: "Vehicles",
          icon: <IconCar size="1rem" />,
          link: "/settings/vehicles",
        },
        {
          label: "Customers",
          icon: <IconUsers size="1rem" />,
          link: "/settings/customers",
        },
        {
          label: "Warehouses",
          icon: <IconBuildingWarehouse size="1rem" />,
          link: "/settings/warehouses",
        },
        {
          label: "Payment Methods",
          icon: <IconCurrencyDollar size="1rem" />,
          link: "/settings/payment-methods",
          privateAccess: ["payment-methods.manage"],
        },
        {
          label: "Integrations",
          icon: <IconPlug size="1rem" />,
          link: "/settings/integrations",
          privateAccess: ["integration.manage"],
        },
      ],
    },
  ];

  return (
    <AppShell header={{ height: 60 }} padding="md">
      {user! && (
        <AppShell.Header>
          <Group p="xs" justify="space-between" align="center">
            <Group gap="xs" align="center">
              {user && (
                <UnstyledButton onClick={() => setDrawerOpened(true)}>
                  <IconMenu2 size={24} color="#4dabf7" />
                </UnstyledButton>
              )}
              <UnstyledButton onClick={() => navigate("/")}>
                <Group gap="xs" align="center">
                  <img src={logo} alt="Logo" style={{ height: 42 }} />
                  <img src={textLogo} alt="Owlara" style={{ height: 32 }} />
                </Group>
              </UnstyledButton>
            </Group>
            <Group>
              <HeaderShipperSelector />
            </Group>
          </Group>
        </AppShell.Header>
      )}

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        title={null}
        size="xs"
        // Add styles to make Drawer content flex column, full height
        styles={{
          content: {
            display: "flex",
            flexDirection: "column",
          },
          body: {
            flexGrow: 1,
            padding: 0,
          },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            // border: "1px solid red",
          }}
        >
          {/* User Info Section - fixed at top */}
          <div style={{ flex: "0 0 auto" }}>
            <Group p="md" style={{ borderBottom: "1px solid #373A40" }}>
              <Avatar color="blue" radius="xl" size="lg">
                {user?.displayName?.charAt(0) || "U"}
              </Avatar>
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {user?.displayName || "User"}
                </Text>
                <Text size="xs" c="dimmed">
                  {user?.email || ""}
                </Text>
                {user?.shipper?.companyName && (
                  <Text size="xs" c="dimmed">
                    {user.shipper.companyName}
                  </Text>
                )}
                <Group gap="xs" mt={4}>
                  <UnstyledButton
                    style={{ fontSize: 12, color: "#228be6", padding: 0 }}
                    onClick={() => {
                      setUserProfileOpen(true);
                    }}
                  >
                    <Group gap={4}>
                      <IconUser size={14} />
                      <Text size="xs" fw={500}>
                        Profile
                      </Text>
                    </Group>
                  </UnstyledButton>
                  <Text size="xs" c="dimmed">
                    |
                  </Text>
                  <UnstyledButton
                    style={{ fontSize: 12, color: "#fa5252", padding: 0 }}
                    onClick={handleLogout}
                  >
                    <Group gap={4}>
                      <IconLogout size={14} />
                      <Text size="xs" fw={500}>
                        Logout
                      </Text>
                    </Group>
                  </UnstyledButton>
                </Group>
              </Stack>
            </Group>
          </div>

          {/* Navigation Menu - scrollable and grows */}
          <div
            style={{
              flex: "1 1 auto",

              overflowY: "auto",
              minHeight: 0,
              padding: 0,
            }}
          >
            <Stack gap={0}>
              {mainMenu.map((menu, mIndex) => (
                <NavigationMenu
                  {...menu}
                  key={mIndex}
                  afterNavigate={() => setDrawerOpened(false)}
                />
              ))}
            </Stack>
          </div>

          {/* Footer Info - fixed at bottom */}
          <div style={{ flex: "0 0 auto" }}>
            <Stack
              gap="xs"
              p="md"
              style={{
                borderTop: "1px solid #373A40",
              }}
            >
              <Group>
                <Text size="xs" c="dimmed">
                  Timezone:
                </Text>
                <Text size="xs">{timezone}</Text>
              </Group>
              <Text size="xs" c="dimmed">
                Version: {config.version}
              </Text>
            </Stack>
          </div>
        </div>
      </Drawer>

      <AppShell.Main
        pl={0}
        pr={0}
        pb={0}
        style={{ position: "relative", display: "flex", overflow: "hidden" }}
      >
        <Container fluid={true} p={0} style={{ flexGrow: 1, overflow: "auto" }}>
          {children}
          <UserPropertiesModal
            user={userProfileOpen ? user ?? undefined : undefined}
            isSelfEdit
            onClose={() => {
              setUserProfileOpen(false);
            }}
            onSave={async (user) => {
              dispatch(setUser(user));
            }}
          />
        </Container>
      </AppShell.Main>
    </AppShell>
  );
};

export default Layout;
